/**
 * XML Document Import Cache
 * Manages template cache and iytxtbs block index for efficient document imports
 */

import { getComplexDocument } from './services/complexDocumentsAPI';
import { fetchPublishedBlocksByMetadataFilter } from './services/complexDocumentsAPI';

// Template cache: Map<language, templateJSON>
const templateCache = new Map();

// iytxtbs Block Index: Map<iytxtbs_value, Map<language, blockData>>
const iytxtbsBlockIndex = new Map();

// Cache initialization state
let isInitialized = false;
let initializationError = null;

/**
 * Initialize template cache by fetching templates for each language
 * Only fetches templates for languages that have provided IDs
 * @param {Object} templateIds - Object with language keys and communicationId values
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<void>}
 */
export const initializeTemplateCache = async (templateIds, onProgress) => {
  try {
    templateCache.clear();
    
    const languages = ['fr', 'nl', 'en', 'de'];
    
    // Filter to only languages with provided IDs
    const languagesToFetch = languages.filter(lang => {
      const id = templateIds[lang];
      return id && id.trim() !== '';
    });
    
    if (languagesToFetch.length === 0) {
      // No templates to fetch, but this is allowed
      if (onProgress) {
        onProgress({
          stage: 'templates-complete',
          templateCount: 0,
        });
      }
      return;
    }
    
    for (let i = 0; i < languagesToFetch.length; i++) {
      const language = languagesToFetch[i];
      const communicationId = templateIds[language];
      
      if (onProgress) {
        onProgress({
          stage: 'fetching-templates',
          language,
          progress: i + 1,
          total: languagesToFetch.length,
        });
      }
      
      const response = await getComplexDocument(communicationId);
      
      if (response.data) {
        templateCache.set(language, response.data);
      } else {
        throw new Error(`Failed to fetch template for language ${language}: ${response.statusCode || 'Unknown error'}`);
      }
    }
    
    if (onProgress) {
      onProgress({
        stage: 'templates-complete',
        templateCount: templateCache.size,
      });
    }
  } catch (error) {
    console.error('Error initializing template cache:', error);
    initializationError = error;
    throw error;
  }
};

/**
 * Initialize iytxtbs block index by fetching all published blocks with iytxtbs meta tag
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<void>}
 */
export const initializeBlockIndex = async (onProgress) => {
  try {
    iytxtbsBlockIndex.clear();
    
    if (onProgress) {
      onProgress({
        stage: 'fetching-blocks',
        message: 'Fetching all published blocks with iytxtbs meta tag...',
      });
    }
    
    // Fetch blocks using the new API with metadata filter
    // Criteria: filter for reusable blocks (type: paragraph)
    // Languages: empty string to fetch all languages
    const criteria = [{ key: "type", value: "paragraph" }];
    const languages = ""; // Empty string to fetch all languages
    const page = 0;
    const size = 2000; // Fetch all blocks just in case
    
    const blocks = await fetchPublishedBlocksByMetadataFilter(
      criteria,
      "",
      languages,
      page,
      size,
      (progress) => {
        if (onProgress) {
          onProgress({
            stage: 'fetching-blocks',
            page: progress.page,
            totalFetched: progress.totalFetched,
          });
        }
      }
    );
    
    // Build index: Map<iytxtbs_value, Map<language, blockData>>
    for (const block of blocks) {
      // Extract iytxtbs value from metadata
      // Check various possible metadata locations
      let iytxtbsValue = null;
      let language = 'en'; // Default to 'en' if not specified
      
      // Check if metadata is in block.metadata array
      if (block.metadata && Array.isArray(block.metadata)) {
        const iytxtbsTag = block.metadata.find(tag => tag.key === 'iytxtbs');
        if (iytxtbsTag && iytxtbsTag.value && iytxtbsTag.value.trim() !== '') {
          iytxtbsValue = iytxtbsTag.value.trim();
        }
        
        // Extract language from metadata if available
        const langTag = block.metadata.find(tag => tag.key === 'language');
        if (langTag && langTag.value) {
          language = langTag.value;
        }
      }
      
      // Check if metadata is an object (not array) - handle different structures
      if (!iytxtbsValue && block.metadata && typeof block.metadata === 'object' && !Array.isArray(block.metadata)) {
        // Try direct property access
        if (block.metadata.iytxtbs && typeof block.metadata.iytxtbs === 'string' && block.metadata.iytxtbs.trim() !== '') {
          iytxtbsValue = block.metadata.iytxtbs.trim();
        }
        // Try nested metaTags array
        if (!iytxtbsValue && block.metadata.metaTags && Array.isArray(block.metadata.metaTags)) {
          const iytxtbsTag = block.metadata.metaTags.find(tag => tag.key === 'iytxtbs');
          if (iytxtbsTag && iytxtbsTag.value && iytxtbsTag.value.trim() !== '') {
            iytxtbsValue = iytxtbsTag.value.trim();
          }
        }
        // Try nested metadata array
        if (!iytxtbsValue && block.metadata.metadata && Array.isArray(block.metadata.metadata)) {
          const iytxtbsTag = block.metadata.metadata.find(tag => tag.key === 'iytxtbs');
          if (iytxtbsTag && iytxtbsTag.value && iytxtbsTag.value.trim() !== '') {
            iytxtbsValue = iytxtbsTag.value.trim();
          }
        }
      }
      
      // Check if metadata is in block.metaTags array
      if (!iytxtbsValue && block.metaTags && Array.isArray(block.metaTags)) {
        const iytxtbsTag = block.metaTags.find(tag => tag.key === 'iytxtbs');
        if (iytxtbsTag && iytxtbsTag.value && iytxtbsTag.value.trim() !== '') {
          iytxtbsValue = iytxtbsTag.value.trim();
        }
      }
      
      // Check if language is directly on the block
      if (block.language) {
        language = block.language;
      }
      
      // Only index blocks that have iytxtbs metadata
      if (iytxtbsValue) {
        if (!iytxtbsBlockIndex.has(iytxtbsValue)) {
          iytxtbsBlockIndex.set(iytxtbsValue, new Map());
        }
        iytxtbsBlockIndex.get(iytxtbsValue).set(language, block);
      }
    }
    
    if (onProgress) {
      onProgress({
        stage: 'blocks-complete',
        blockCount: blocks.length,
        indexedCount: iytxtbsBlockIndex.size,
      });
    }

    console.log(" iytxtbsBlockIndex ::  ", iytxtbsBlockIndex);
  } catch (error) {
    console.error('Error initializing block index:', error);
    initializationError = error;
    throw error;
  }
};

/**
 * Initialize both template cache and block index
 * @param {Object} templateIds - Object with language keys and communicationId values
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<void>}
 */
export const initializeCache = async (templateIds, onProgress) => {
  try {
    isInitialized = false;
    initializationError = null;
    
    if (onProgress) {
      onProgress({
        stage: 'initializing',
        message: 'Starting cache initialization...',
      });
    }
    
    // Initialize templates first (only for languages with provided IDs)
    await initializeTemplateCache(templateIds || {}, onProgress);
    
    // Then initialize block index (only if using referenced blocks)
    await initializeBlockIndex(onProgress);
    
    isInitialized = true;
    
    if (onProgress) {
      onProgress({
        stage: 'complete',
        message: 'Cache initialization complete',
        templateCount: templateCache.size,
        blockIndexCount: iytxtbsBlockIndex.size,
      });
    }
  } catch (error) {
    isInitialized = false;
    initializationError = error;
    if (onProgress) {
      onProgress({
        stage: 'error',
        error: error.message,
      });
    }
    throw error;
  }
};

/**
 * Get cached template for a specific language (deep cloned)
 * @param {string} language - Language code (fr, nl, en, de)
 * @returns {Object|null} Deep cloned template JSON or null if not found
 */
export const getTemplate = (language) => {
  const template = templateCache.get(language);
  if (!template) {
    return null;
  }
  // Deep clone to avoid mutating the cached template
  return JSON.parse(JSON.stringify(template));
};

/**
 * Lookup block by iytxtbs value and language
 * @param {string} iytxtbsValue - iytxtbs value to lookup
 * @param {string} language - Language code (fr, nl, en, de)
 * @returns {Object|null} Block data or null if not found
 */
export const lookupBlockByIytxtbs = (iytxtbsValue, language) => {
  if (!iytxtbsValue) {
    return null;
  }
  
  const languageMap = iytxtbsBlockIndex.get(iytxtbsValue);
  if (!languageMap) {
    return null;
  }
  
  return languageMap.get(language) || null;
};

/**
 * Check if cache is initialized
 * @returns {boolean}
 */
export const isCacheInitialized = () => {
  return isInitialized;
};

/**
 * Get cache status
 * @returns {Object} Cache status information
 */
export const getCacheStatus = () => {
  return {
    isInitialized,
    initializationError: initializationError?.message || null,
    templateCount: templateCache.size,
    blockIndexCount: iytxtbsBlockIndex.size,
    availableLanguages: Array.from(templateCache.keys()),
  };
};

/**
 * Clear all caches
 */
export const clearCache = () => {
  templateCache.clear();
  iytxtbsBlockIndex.clear();
  isInitialized = false;
  initializationError = null;
};

